import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Code2, Database, Cloud, Shield, Gauge, Users } from 'lucide-react';
import PageTransition from '../../components/ui/PageTransition';
import HeroSection from '../../components/sections/HeroSection';
import SectionHeading from '../../components/ui/SectionHeading';
import CtaSection from '../../components/sections/CtaSection';
import Button from '../../components/ui/Button';

const WebApplicationsPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Web Application Development | Consiliuma - Nottingham Development Experts</title>
        <meta name="description" content="Expert web application development in Nottingham. Build powerful, scalable, and secure web applications that transform your business. British expertise with global reach." />
        <meta name="keywords" content="Web Application Development Nottingham, Custom Web Apps, Enterprise Web Applications, Full Stack Development, Software Development UK" />
        <link rel="canonical" href="/services/web-applications" />
      </Helmet>

      <HeroSection
        title="Web Application Development"
        subtitle="Transform your business with powerful custom web applications"
        ctaText="Start Your Project"
        ctaLink="/quote"
        backgroundImage="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg"
      />

      {/* Core Features */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Comprehensive Web Application Solutions"
            subtitle="Building powerful applications that solve complex business challenges"
            center
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Code2 size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Custom Development</h3>
              <p className="text-secondary">
                Tailored solutions built specifically for your business needs and workflows.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Database size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Scalable Architecture</h3>
              <p className="text-secondary">
                Built to grow with your business using modern, scalable technologies.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Cloud size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Cloud Integration</h3>
              <p className="text-secondary">
                Leverage cloud technologies for reliability, scalability, and global reach.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Shield size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Enterprise Security</h3>
              <p className="text-secondary">
                Robust security measures to protect your data and users.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Gauge size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">High Performance</h3>
              <p className="text-secondary">
                Optimized for speed and efficiency across all user scenarios.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Users size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">User-Centric Design</h3>
              <p className="text-secondary">
                Intuitive interfaces that enhance user productivity and satisfaction.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Types of Applications */}
      <section className="py-16 md:py-24 bg-background-alt">
        <div className="container-custom">
          <SectionHeading
            title="Web Application Solutions"
            subtitle="Transforming businesses across industries"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Business Management Systems</h3>
              <p className="text-secondary mb-4">
                Streamline operations with custom management solutions.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• CRM systems</li>
                <li>• Project management tools</li>
                <li>• Resource planning</li>
                <li>• Workflow automation</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Customer Portals</h3>
              <p className="text-secondary mb-4">
                Enhance customer service with self-service portals.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Account management</li>
                <li>• Service requests</li>
                <li>• Document access</li>
                <li>• Communication tools</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Data Analytics Platforms</h3>
              <p className="text-secondary mb-4">
                Turn data into insights with analytics solutions.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Real-time dashboards</li>
                <li>• Custom reports</li>
                <li>• Data visualization</li>
                <li>• Predictive analytics</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Our Development Process"
                subtitle="A systematic approach to building robust applications"
              />
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Discovery & Planning</h3>
                    <p className="text-secondary">
                      Understanding your needs and creating detailed technical specifications.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Architecture Design</h3>
                    <p className="text-secondary">
                      Creating a scalable and maintainable application architecture.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Development</h3>
                    <p className="text-secondary">
                      Agile development with regular updates and milestone reviews.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Testing</h3>
                    <p className="text-secondary">
                      Comprehensive testing including unit tests, integration tests, and user acceptance testing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">5</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Deployment</h3>
                    <p className="text-secondary">
                      Careful deployment with monitoring and immediate support.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button to="/quote" variant="primary">
                  Start Your Project
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/3183153/pexels-photo-3183153.jpeg"
                alt="Web application development process"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 md:py-24 bg-background-alt">
        <div className="container-custom">
          <SectionHeading
            title="Our Technology Stack"
            subtitle="Modern technologies for robust applications"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Front-End</h3>
              <ul className="space-y-2 text-secondary">
                <li>• React.js</li>
                <li>• Vue.js</li>
                <li>• TypeScript</li>
                <li>• Next.js</li>
                <li>• Tailwind CSS</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Back-End</h3>
              <ul className="space-y-2 text-secondary">
                <li>• Node.js</li>
                <li>• Python</li>
                <li>• Java</li>
                <li>• .NET Core</li>
                <li>• GraphQL</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Infrastructure</h3>
              <ul className="space-y-2 text-secondary">
                <li>• AWS</li>
                <li>• Docker</li>
                <li>• Kubernetes</li>
                <li>• CI/CD</li>
                <li>• Microservices</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Ready to Build Your Web Application?"
        subtitle="Let's create something exceptional together"
        buttonText="Get Started"
        buttonLink="/quote"
      />
    </PageTransition>
  );
};

export default WebApplicationsPage;